import type { WorkspaceSurface, SurfaceKey } from '../../../../methods/src/common/ideaWorkspace';

export type { WorkspaceSurface, SurfaceKey };

/**
 * Idea scope: the shared rules behind the idea rail, the phone idea bar and
 * Idea map, and the "Next for this idea" band. Every consumer reads the same
 * helpers here so they can never disagree about where the member is or what
 * comes next.
 */

// 'businessPlanning' is the idea's Business Planning hub (customer discovery, canvas and progress tracking
// together): not one step and not the Command Center, so no step row is highlighted there.
export type ScopeKey = SurfaceKey | 'overview' | 'businessPlanning' | 'notes' | 'log' | 'packet';

export const SURFACE_LABEL: Record<ScopeKey, string> = {
  overview: 'Command Center',
  businessPlanning: 'Business Planning',
  notes: 'Notes',
  log: 'Invention log',
  packet: 'Ready to share',
  productStrategist: 'Product Strategist',
  customerDiscovery: 'Customer Discovery',
  signals: 'Signals',
  ipConsultant: 'IP Consultant',
  priorArt: 'Prior-art search',
  design: 'Design breakdown',
  sketch: 'Design Sketch',
  financialAnalyst: 'Financial Analyst',
  canvas: 'Business Model Canvas',
  tracker: 'Progress Tracking',
};

/** The four phases of the climb, in list order. */
export const PHASES: Array<{ id: string; label: string; keys: SurfaceKey[] }> = [
  { id: 'need', label: 'Need', keys: ['productStrategist', 'customerDiscovery', 'signals'] },
  { id: 'invention', label: 'Invention', keys: ['ipConsultant', 'priorArt'] },
  { id: 'product', label: 'Product', keys: ['design', 'sketch'] },
  { id: 'business', label: 'Business', keys: ['financialAnalyst', 'canvas', 'tracker'] },
];

/** The order "Next" walks. Signals is ongoing monitoring, never a next step. */
const NEXT_ORDER: SurfaceKey[] = [
  'productStrategist',
  'customerDiscovery',
  'ipConsultant',
  'priorArt',
  'design',
  'sketch',
  'financialAnalyst',
  'canvas',
];

/** One plain sentence per step, for the Next band and the phone card. */
export const NEXT_COPY: Record<SurfaceKey, string> = {
  productStrategist: 'Find out who needs this and what they\'d pay, using real market data.',
  customerDiscovery: 'Turn your Product Strategist memo into interview questions for customers.',
  signals: 'Track demand, competitors and new patents for this idea.',
  ipConsultant: 'See whether the idea can be protected, by checking it against prior art from 100+ patent offices.',
  priorArt: 'Build a professional search plan for the prior art closest to your idea.',
  design: 'EntreDash drafts the invention\'s parts and materials for you to review and edit.',
  sketch: 'Turn your parts list into a drawing brief and numbered sketches.',
  financialAnalyst: 'Work through pricing, costs and market size to see whether the numbers hold.',
  canvas: 'Pull all three memos into one business model canvas.',
  tracker: 'Every memo\'s next steps as one checklist, with due dates and priorities.',
};

/** Idea id from a path inside an idea (`/ideas/:id` and below), else null. */
export function ideaIdFromPath(path: string): string | null {
  const m = /^\/ideas\/([^/?#]+)/.exec(path);
  if (!m || m[1] === 'new') return null;
  return m[1];
}

/** Which step a path belongs to (every sub-route of a step counts). */
export function scopeKeyForPath(path: string): ScopeKey {
  const rest = path.replace(/^\/ideas\/[^/]+/, '');
  if (/^\/needs-discovery/.test(rest)) return 'productStrategist';
  if (/^\/customer-discovery/.test(rest)) return 'customerDiscovery';
  if (/^\/signals/.test(rest)) return 'signals';
  if (/^\/(session|memos)(\/|$)/.test(rest)) return 'ipConsultant';
  if (/^\/search-(strategy|report)/.test(rest)) return 'priorArt';
  if (/^\/design(\/|$)/.test(rest)) return 'design';
  if (/^\/sketch/.test(rest)) return 'sketch';
  if (/^\/business-case/.test(rest)) return 'financialAnalyst';
  if (/^\/business-canvas/.test(rest)) return 'canvas';
  if (/^\/progress/.test(rest)) return 'tracker';
  if (/^\/business-planning/.test(rest)) return 'businessPlanning';
  if (/^\/notes/.test(rest)) return 'notes';
  if (/^\/log(\/|$)/.test(rest)) return 'log';
  if (/^\/packet(\/|$)/.test(rest)) return 'packet';
  return 'overview';
}

/** The equivalent place in another idea (switching keeps the member on the same step). */
export function sameStepIn(path: string, otherIdeaId: string): string {
  const key = scopeKeyForPath(path);
  const rest = path.replace(/^\/ideas\/[^/]+/, '');
  // Memo URLs carry a version the other idea may not have: go to the step's entry.
  if (key === 'overview' || /memo/.test(rest)) {
    const entry: Partial<Record<ScopeKey, string>> = {
      productStrategist: '/needs-discovery',
      ipConsultant: '/session',
      financialAnalyst: '/business-case',
    };
    return `/ideas/${otherIdeaId}${entry[key] ?? ''}`;
  }
  return `/ideas/${otherIdeaId}${rest.split('?')[0]}`;
}

/**
 * The next step for this idea: the first step in progress, otherwise the first
 * step not started that's available. `exclude` drops the step just finished.
 * Null when every step is done.
 */
export function nextStepFor(surfaces: WorkspaceSurface[], exclude?: ScopeKey): WorkspaceSurface | null {
  const byKey = new Map(surfaces.map((s) => [s.key, s]));
  const ordered = NEXT_ORDER.map((k) => byKey.get(k)).filter((s): s is WorkspaceSurface => !!s && s.key !== exclude);
  return ordered.find((s) => s.status === 'doing') ?? ordered.find((s) => s.status === 'todo' && !s.gated) ?? null;
}

/** Button label for moving to a step. */
export function actionLabel(s: WorkspaceSurface): string {
  const name = SURFACE_LABEL[s.key];
  if (s.status === 'doing') return `Continue ${name}`;
  if (s.status === 'done') return `Open ${name}`;
  return `Start ${name}`;
}

/**
 * Where a quick note is being written, in words, for its link back: "Financial Analyst memo v2",
 * "3D model", "Dashboard". Inside an idea it names the step; elsewhere the page.
 */
export function captureSourceFor(path: string): string {
  const clean = path.split(/[?#]/)[0];
  const memo = /^\/ideas\/[^/]+\/(needs-discovery-memo|business-case-memo|memos)\/(\d+)/.exec(clean);
  if (memo) {
    const who = memo[1] === 'needs-discovery-memo' ? 'Product Strategist' : memo[1] === 'business-case-memo' ? 'Financial Analyst' : 'IP Consultant';
    return `${who} memo v${memo[2]}`;
  }
  if (ideaIdFromPath(clean)) {
    if (/\/sketch/.test(clean) && /model|3d/i.test(path)) return '3D model';
    const key = scopeKeyForPath(clean);
    if (key === 'productStrategist' || key === 'ipConsultant' || key === 'financialAnalyst') return `${SURFACE_LABEL[key]} session`;
    return SURFACE_LABEL[key];
  }
  const top: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/spark': 'Spark',
    '/ideas': 'Ideas',
    '/strategy': 'Advisory board',
    '/notes': 'Notes',
    '/intelligence': 'Intelligence Center',
    '/signals': 'Signals',
    '/business-planning': 'Business Planning',
    '/invention-log': 'Design Workspace',
  };
  return top[clean] ?? 'EntreDash';
}
